import { Injector, ModelSignal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_ZONES, InputFormatType, NavigationOptionsType } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Subject, debounceTime, merge, startWith, takeUntil } from 'rxjs';
import { SetFormActionWithId } from '../../actions/form-submission.action';
import { WorkflowFormGuard } from '../../services/workflow-forms/workflow-form.guard';
import { WorkflowFormsService } from '../../services/workflow-forms/workflow-forms.service';
import { FormActionState } from '../../state/form-submision.state';
import { createRedirectionUrlFromNavigationOptions } from '../utils';

export function verifyEmailPhoneButton(
  injector: Injector,
  type: InputFormatType | undefined,
  widgetNavigationOptions: NavigationOptionsType,
  formControlName: string, // this is the form control name and id
  parentFormID: string,
  nestedFormId: string,
  phoneNumberAttachToInput: boolean,
  countryCodeFormControl: AbstractControl<any, any> | null | undefined,
  mainFormControl: AbstractControl<any, any> | null | undefined,
  countryCodeFormControlName?: string | undefined,
) {
  const router = injector.get(Router);
  const store = injector.get(Store);
  const workflowFormsService = injector.get(WorkflowFormsService);
  const activeRoute = injector.get(ActivatedRoute);
  const currentZoneFromRoute = activeRoute?.snapshot?.params?.['zone'];
  const isLoggedOutZone =
    currentZoneFromRoute === APP_ZONES.ONBOARDING ||
    currentZoneFromRoute === APP_ZONES.ORGANISATION_ONBOARDING ||
    currentZoneFromRoute === APP_ZONES.ORGANISATION_VERIFICATION;

  const isValid = isInputValid(
    type,
    mainFormControl,
    phoneNumberAttachToInput,
    countryCodeFormControl,
    countryCodeFormControlName,
  );

  if (!isValid) {
    console.warn('Verification blocked: Input is invalid.');
    return;
  }

  const formValue = workflowFormsService.getFormPart(nestedFormId)?.value;
  if (!formValue) return;

  const payload: any = { type: '', ...formValue };

  // Add form control value
  if (formControlName in formValue) {
    payload[formControlName] = formValue[formControlName];
  }

  // Add country code value if present
  if (countryCodeFormControlName && countryCodeFormControlName in formValue && !phoneNumberAttachToInput) {
    payload[countryCodeFormControlName] = formValue[countryCodeFormControlName];
  }

  store.dispatch(
    new SetFormActionWithId(
      {
        type: '',
        formData: { ...payload },
      },
      parentFormID,
    ),
  );

  const navigationOptions: NavigationOptionsType = {
    navigationOption: widgetNavigationOptions['navigationOption'],
    zone: widgetNavigationOptions?.zone,
    pageUrl: widgetNavigationOptions?.['pageUrl'],
    role: widgetNavigationOptions?.role,
    pageId: widgetNavigationOptions?.['pageId'] ?? '',
    tabRef: widgetNavigationOptions?.['tabRef'] ?? '',
    pageTransition: widgetNavigationOptions?.['pageTransition'] ?? 'forward',
  };

  const url = createRedirectionUrlFromNavigationOptions(navigationOptions, isLoggedOutZone);

  if (url) {
    WorkflowFormGuard.disableFormValidation();
    router.navigateByUrl(url, { state: { formControlId: formControlName } });
  }
}

/**
 * Validates an input based on its format type (e.g., email, phone number).
 *
 * This function checks the validity of a given input field using its associated
 * Angular form control. Special handling is provided for phone number inputs
 * where the country code may be a separate field.
 *
 * @param type - The expected input format type (EMAIL or PHONE_NUMBER).
 * @param mainFormControl - The form control associated with the main input field.
 * @param phoneNumberAttachToInput - Whether the phone number includes the country code inline.
 * @param countryCodeFormControl - The form control for the country code field (if separate).
 * @param countryCodeFormControlName - The name of the country code control (used if not inline).
 * @returns A boolean indicating whether the input is valid.
 */
function isInputValid(
  type: InputFormatType | undefined,
  mainFormControl: any,
  phoneNumberAttachToInput: boolean,
  countryCodeFormControl: any,
  countryCodeFormControlName?: string,
): boolean {
  if (!type || !mainFormControl) return false;

  switch (type) {
    case InputFormatType.EMAIL: {
      return mainFormControl?.valid;
    }

    case InputFormatType.PHONE_NUMBER: {
      const isPhoneValid = mainFormControl?.valid;

      let isCountryCodeValid = true;
      // If the phone number does NOT include the country code inline,
      // validate the separate country code form control
      if (!phoneNumberAttachToInput && countryCodeFormControlName) {
        isCountryCodeValid = countryCodeFormControl?.valid && !!countryCodeFormControl?.value;
      }

      return isPhoneValid && isCountryCodeValid;
    }

    default:
      return true; // Assume other input types are valid by default
  }
}

interface ControlMap {
  name: string;
  control: AbstractControl;
  prevValue: string;
  prevVerifiedValue: string;
  // Flag to track if this control has changed (used for phone)
  hasChanged: boolean;
}

interface VerificationConfig {
  // Unique name for the verification field (e.g., 'isEmailVerified')
  verifiedField: string;
  // Array of controls that need to be monitored for changes
  inputControls: ControlMap[];
  // Auxiliary controls that must be cleared/synced (e.g., verifiedEmail, verifiedCountryCode)
  auxiliaryControls: {
    key: string;
    control: AbstractControl;
  }[];
}

/**
 * Executes the generic flow for field verification (Email, Phone, etc.).
 * Handles control initialization, state retrieval, validation toggling, and reactive listening.
 */
export const handleInputFieldVerificationFlow = (
  injector: Injector,
  destroy$: Subject<void>,
  showVerifyButton: ModelSignal<boolean>,
  widgetParentFormId: string,
  formGroup: FormGroup,
  config: VerificationConfig,
): void => {
  const { verifiedField, inputControls, auxiliaryControls } = config;
  if (!showVerifyButton()) return;

  const store = injector.get(Store);
  const parentFormId = widgetParentFormId ?? '';

  // --- Auxiliary Control Initialization ---

  // Define all controls that need to be ensured in the FormGroup
  const allControls = [{ key: verifiedField, control: new FormControl('') }, ...auxiliaryControls];

  allControls.forEach((item) => {
    if (!formGroup.contains(item.key)) {
      formGroup.addControl(item.key, new FormControl(''));
    }
  });

  const verifiedCtrl = formGroup?.get(verifiedField);

  // --- State and Verification Helpers ---
  let wasVerified = !!store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId))
    ?.formData?.[verifiedField];

  // update the prevVerifiedValue with control value when input is verified and has valid value
  if (wasVerified && !!verifiedCtrl?.value) {
    inputControls.forEach((item) => {
      item.prevVerifiedValue = item.prevValue;
    });
  }

  /**
   * Determines if any monitored control has changed relative to its last verified state.
   * Updates the 'hasChanged' flags on the inputControls array as a side effect.
   */
  const determineNeedsVerification = (): boolean => {
    let requiresVerification = false;

    inputControls.forEach((item, index) => {
      const currentValue = item.control.value;
      let valueChanged: boolean;

      const data = store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId))?.formData;
      if (data?.[auxiliaryControls[index]?.key] === item.control.value) {
        verifiedCtrl?.setValue(true);
        item.prevVerifiedValue = item.control.value;
        formGroup.get(auxiliaryControls[index]?.key)?.setValue(item.control.value);
      }

      if (item.prevVerifiedValue) {
        valueChanged = currentValue !== item.prevVerifiedValue;
        wasVerified = currentValue === item.prevVerifiedValue;
      } else {
        valueChanged = currentValue !== item.prevValue;
      }

      item.hasChanged = valueChanged;
      if (valueChanged) requiresVerification = true;
    });

    return requiresVerification;
  };

  /**
   * Updates validation, synchronizes auxiliary controls, and sends state update to the store.
   */
  const updateVerification = (needsVerification: boolean): void => {
    showVerifyButton.set(needsVerification);

    if (needsVerification) {
      verifiedCtrl?.setValue(null);
      verifiedCtrl?.setValidators([Validators.required]);
    } else {
      verifiedCtrl?.clearValidators();
      verifiedCtrl?.setValue(true);

      // Sync current values to their auxiliary (verified) counterparts
      inputControls.forEach((item, index) => {
        if (!auxiliaryControls[index]?.control) {
          auxiliaryControls[index]?.control ??
            formGroup.addControl(auxiliaryControls[index]?.key, new FormControl(''));
        }
        formGroup.get(auxiliaryControls[index]?.key)?.setValue(item.control.value);
      });
    }

    // Update validity for all controls
    [{ key: verifiedField, control: verifiedCtrl }, ...auxiliaryControls].forEach((ctrl) =>
      ctrl.control?.updateValueAndValidity({ emitEvent: false }),
    );

    const currentFormValues = store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId));
    const verifiedAtFieldObj: { [key: string]: string } = {};
    const verifiedAtFieldName = verifiedField.toLowerCase().includes('email')
      ? 'emailVerifiedAt'
      : 'phoneVerifiedAt';
    if (
      needsVerification &&
      (currentFormValues?.formData?.[verifiedAtFieldName] ||
        !currentFormValues?.formData?.[verifiedAtFieldName])
    ) {
      // Clear the verification timestamp if verification is needed
      verifiedAtFieldObj[verifiedAtFieldName] = '';
    } else if (!needsVerification && currentFormValues?.formData?.[verifiedAtFieldName]) {
      // assign existing value if data is already present
      verifiedAtFieldObj[verifiedAtFieldName] = currentFormValues.formData[verifiedAtFieldName];
    } else if (!needsVerification && !currentFormValues?.formData?.[verifiedAtFieldName]) {
      // if field do not exist add that field
      verifiedAtFieldObj[verifiedAtFieldName] = new Date().toISOString();
    }

    const fieldsToUpdate = {
      [verifiedField]: needsVerification ? false : true,
      ...verifiedAtFieldObj,
    };

    store.dispatch(
      new SetFormActionWithId(
        {
          type: '',
          formData: fieldsToUpdate,
        },
        parentFormId,
      ),
    );
  };

  // Hide the button first. The field value may still be loading at this point, so deciding now can
  // briefly show the button by mistake. We let the listener below decide once the value is ready.
  showVerifyButton.set(false);

  // Watch every input for value changes.
  const valueChangeStreams = inputControls.map((item) => item.control.valueChanges);

  // startWith(null): run the check once on load even if the value never changes.
  // debounceTime(300): wait for the value to settle before deciding (also avoids running on every keystroke).
  merge(...valueChangeStreams)
    .pipe(startWith(null), debounceTime(300), takeUntil(destroy$))
    .subscribe(() => {
      const needsVerification = determineNeedsVerification();
      updateVerification(!wasVerified || needsVerification);
    });
};

export const handleEmailVerification = (
  injector: Injector,
  destroy$: Subject<void>,
  showEmailVerifyButton: ModelSignal<boolean>,
  widgetParentFormId: string,
  formGroup: FormGroup,
  formControlName: string,
): void => {
  const VERIFIED_FIELD = 'isEmailVerified';
  const INPUT_VERIFIED_EMAIL_FIELD = 'verifiedEmail';

  const store = injector.get(Store);
  const parentFormId = widgetParentFormId ?? '';
  const prevData =
    store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId))?.formData ?? {};

  const mainCtrl = formGroup?.get(formControlName) as AbstractControl;
  const verifiedEmailCtrl = formGroup?.get(INPUT_VERIFIED_EMAIL_FIELD) as AbstractControl;

  const emailConfig: VerificationConfig = {
    verifiedField: VERIFIED_FIELD,
    inputControls: [
      {
        name: formControlName,
        control: mainCtrl,
        prevValue: prevData?.[formControlName],
        prevVerifiedValue: prevData?.[INPUT_VERIFIED_EMAIL_FIELD],
        hasChanged: false,
      },
    ],
    auxiliaryControls: [
      {
        key: INPUT_VERIFIED_EMAIL_FIELD,
        control: verifiedEmailCtrl,
      },
    ],
  };

  handleInputFieldVerificationFlow(
    injector,
    destroy$,
    showEmailVerifyButton,
    widgetParentFormId,
    formGroup,
    emailConfig,
  );
};

export const handlePhoneVerification = (
  injector: Injector,
  destroy$: Subject<void>,
  showPhoneVerifyButton: ModelSignal<boolean>,
  widgetParentFormId: string,
  formGroup: FormGroup,
  formControlName: string,
  countryCodeKey: string,
  attachToInput: boolean,
): void => {
  const VERIFIED_FIELD = 'isPhoneVerified';
  const INPUT_VERIFIED_PHONE_FIELD = 'verifiedPhone';
  const INPUT_VERIFIED_COUNTRY_CODE_FIELD = 'verifiedCountryCode';
  const COUNTRY_KEY = countryCodeKey ?? 'countryCode';

  const store = injector.get(Store);
  const parentFormId = widgetParentFormId ?? '';
  const prevData =
    store.selectSnapshot(FormActionState.getFormActionStateWithId(parentFormId))?.formData ?? {};

  const mainCtrl = formGroup?.get(formControlName) as AbstractControl;
  const countryCtrl = formGroup?.get(COUNTRY_KEY) as AbstractControl;
  const verifiedPhoneCtrl = formGroup?.get(INPUT_VERIFIED_PHONE_FIELD) as AbstractControl;
  const verifiedCountryCtrl = formGroup?.get(INPUT_VERIFIED_COUNTRY_CODE_FIELD) as AbstractControl;

  const phoneConfig: VerificationConfig = {
    verifiedField: VERIFIED_FIELD,
    inputControls: [
      // Always include the main phone number
      {
        name: formControlName,
        control: mainCtrl,
        prevValue: prevData?.[formControlName],
        prevVerifiedValue: prevData?.[INPUT_VERIFIED_PHONE_FIELD],
        hasChanged: false,
      },
      // Include country code only if it's separate
      ...(!attachToInput
        ? [
            {
              name: COUNTRY_KEY,
              control: countryCtrl,
              prevValue: prevData?.[COUNTRY_KEY],
              prevVerifiedValue: prevData?.[INPUT_VERIFIED_COUNTRY_CODE_FIELD],
              hasChanged: false,
            },
          ]
        : []),
    ],
    auxiliaryControls: [
      {
        key: INPUT_VERIFIED_PHONE_FIELD,
        control: verifiedPhoneCtrl,
      },
      ...(!attachToInput
        ? [
            {
              key: INPUT_VERIFIED_COUNTRY_CODE_FIELD,
              control: verifiedCountryCtrl,
            },
          ]
        : []),
    ],
  };

  handleInputFieldVerificationFlow(
    injector,
    destroy$,
    showPhoneVerifyButton,
    widgetParentFormId,
    formGroup,
    phoneConfig,
  );
};

/**
 * Scrolls smoothly to an element by ID and updates the URL fragment
 * without adding a new browser history entry.
 *
 * Why needed?
 * - After `location.back()` navigation, the browser state is not immediately stable.
 * - `setTimeout()` ensures Angular routing finishes before updating the fragment.
 * - `replaceUrl: true` prevents creating an extra history entry
 *   which avoids a "double-back" bug.
 *
 * Also applies a brief delay before focusing the input to fix keyboard focus
 * issues in Safari and iOS WebView environments.
 */
export const scrollAndUpdateFragment = (router: Router) => {
  const formControlId = history.state?.formControlId;
  if (!formControlId) return;
  setTimeout(() => {
    router.navigate([], {
      fragment: formControlId,
      replaceUrl: true, // prevents extra back navigation step
    });
  }, 50);
};
